/**
 * <s-dialog> — Dialog Web Component
 * Handles backdrop-click-to-close for native <dialog> modals.
 * Open/close and password visibility are driven by Invoker Commands.
 *
 * Focus management (WCAG 2.4.3):
 *   Captures the native show-modal command source or the active element for a
 *   programmatic showModal(), then restores the original or same-ID replacement.
 *
 * Attributes:
 *   close-on-navigate — close the dialog when an <a> inside it is clicked
 *                       (useful with client-side navigation)
 *   open-on-connect   — open modally after connection (for server-returned feedback)
 *   reset-on-close    — reset forms, password visibility, and marked error state
 *   history-steps     — each opened drawer becomes a Back-button step: Back closes the
 *                       top drawer only, and returning from a page opened inside a
 *                       drawer reopens the drawers that were open (see HistorySteps)
 *
 * Commands:
 *   --toggle-password — toggle the password input named by event.source aria-controls
 */
/**
 * Back-button steps for drawers marked history-steps (e.g. ☰ menu → Men → category page).
 * The list of open drawer ids is kept in history.state, one entry per opened drawer:
 * - opening a drawer pushes an entry; closing it from the UI goes back one entry;
 * - Back closes drawers missing from the new entry (only the top one);
 * - returning to a page (Back from a category page, or bfcache) reopens the drawers
 *   recorded in its entry, in order, so each further Back closes one of them.
 */
const HistorySteps = {
  syncing: false,

  openIds() {
    return history.state?.lxDialogs ?? [];
  },

  dialog(id) {
    const dialog = document.getElementById(id);
    return dialog instanceof HTMLDialogElement && dialog.closest('s-dialog[history-steps]') ? dialog : null;
  },

  opened(id) {
    if (this.syncing) return;
    const ids = this.openIds().filter((openId) => openId !== id);
    history.pushState({ ...(history.state ?? {}), lxDialogs: [...ids, id] }, '');
  },

  closedByUser(id) {
    if (this.syncing || this.leaving) return;
    // Wait a tick: if this close came from tapping a link, the page is leaving and
    // going back would cancel the new page from opening.
    setTimeout(() => {
      if (this.leaving) return;
      if (this.openIds().at(-1) === id) history.back();
    }, 0);
  },

  // True from the moment a shopper taps a link to another page.
  leaving: false,

  // Make the open drawers match the current history entry.
  sync() {
    const wanted = this.openIds();
    this.syncing = true;
    try {
      for (const dialog of document.querySelectorAll('s-dialog[history-steps] > dialog[open]')) {
        if (!wanted.includes(dialog.id)) dialog.close();
      }
      for (const id of wanted) {
        const dialog = this.dialog(id);
        if (dialog && !dialog.open) dialog.showModal();
      }
    } finally {
      this.syncing = false;
    }
  },
};

window.addEventListener('popstate', () => HistorySteps.sync());

// Any same-tab link tap to a real page marks the page as leaving (capture: runs first).
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const href = link.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('javascript')) return;
  HistorySteps.leaving = true;
}, true);
// A link whose tap was handled in-page (no navigation) doesn't count as leaving.
window.addEventListener('click', (event) => {
  if (event.defaultPrevented) HistorySteps.leaving = false;
});
window.addEventListener('pagehide', () => { HistorySteps.leaving = true; });

// Coming back to this page: reopen the recorded drawers right away (not after every
// image has loaded), and again when restored from the back-forward cache.
window.addEventListener('pageshow', (event) => {
  HistorySteps.leaving = false;
  if (event.persisted && HistorySteps.openIds().length > 0) HistorySteps.sync();
});
if (HistorySteps.openIds().length > 0) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => HistorySteps.sync(), { once: true });
  } else {
    HistorySteps.sync();
  }
}

class SDialog extends HTMLElement {
  #dialog;
  #ac;
  #triggerElement = null;
  #triggerElementId = null;
  #originalShowModal = null;
  #navigating = false;
  #userClosing = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#interceptShowModal();

    // CommandEvent does not bubble, so capture its source on the controlled dialog.
    this.#dialog.addEventListener('command', (e) => {
      if (!(e.source instanceof HTMLElement)) return;

      if (e.command === 'show-modal') {
        this.#triggerElement = e.source;
        this.#triggerElementId = e.source.id || null;
        // Invoker commands open the dialog natively (not via showModal), so record after it opens.
        setTimeout(() => this.#recordHistoryStep(), 0);
        return;
      }

      if (e.command === 'close' || e.command === 'request-close') {
        this.#userClosing = true;
        return;
      }

      if (e.command === '--toggle-password') {
        e.preventDefault();
        this.#togglePassword(e.source);
      }
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (this.hasAttribute('reset-on-close')) this.#resetContent();
      this.#restoreFocus();
      // Only a close the shopper asked for (← button, tapping outside, Esc / phone back)
      // steps history back. Anything else, like tapping a category link, never does.
      if (this.hasAttribute('history-steps') && this.#userClosing && !this.#navigating) HistorySteps.closedByUser(this.#dialog.id);
      this.#userClosing = false;
      this.#navigating = false;
    }, opts);

    this.#dialog.addEventListener('cancel', () => { this.#userClosing = true; }, opts);

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) {
        this.#userClosing = true;
        this.#dialog.close();
      }
    }, opts);

    this.#setupBreakpointListener();

    if (this.hasAttribute('close-on-navigate')) {
      this.addEventListener('click', (e) => {
        if (e.target.closest('a')) {
          // Leaving the page: keep the history entry so Back reopens this drawer.
          this.#navigating = true;
          this.#dialog.close();
        }
      }, opts);
    }

    // Menu drawers: start loading a page the moment a finger touches its link, so it
    // opens as soon as the tap ends. The drawer stays on screen until the new page shows.
    if (this.hasAttribute('history-steps')) {
      const prefetched = new Set();
      this.addEventListener('pointerdown', (e) => {
        const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
        if (!link || link.origin !== window.location.origin || prefetched.has(link.href)) return;
        prefetched.add(link.href);
        const hint = document.createElement('link');
        hint.rel = 'prefetch';
        hint.href = link.href;
        document.head.append(hint);
      }, { ...opts, passive: true });
    }

    if (this.hasAttribute('open-on-connect') && !this.#dialog.open) {
      this.#dialog.showModal();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    // Restore original showModal to support reconnect (DOM move)
    if (this.#dialog && this.#originalShowModal) {
      this.#dialog.showModal = this.#originalShowModal;
      this.#originalShowModal = null;
    }
  }

  #interceptShowModal() {
    if (this.#originalShowModal) return;
    this.#originalShowModal = this.#dialog.showModal.bind(this.#dialog);
    this.#dialog.showModal = (...args) => {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement
        && activeElement !== document.body
        && activeElement !== document.documentElement
        && !this.#triggerElement
      ) {
        this.#triggerElement = activeElement;
        this.#triggerElementId = activeElement.id || null;
      }
      const result = this.#originalShowModal(...args);
      this.#recordHistoryStep();
      return result;
    };
  }

  #recordHistoryStep() {
    if (!this.hasAttribute('history-steps') || !this.#dialog.open || !this.#dialog.id) return;
    if (HistorySteps.openIds().at(-1) === this.#dialog.id) return;
    HistorySteps.opened(this.#dialog.id);
  }

  #togglePassword(source) {
    const inputId = source.getAttribute('aria-controls');
    const input = inputId ? document.getElementById(inputId) : null;
    if (!(input instanceof HTMLInputElement) || !this.#dialog.contains(input)) return;
    if (input.type !== 'password' && input.type !== 'text') return;

    const reveal = input.type === 'password';
    const label = reveal ? source.dataset.hideLabel : source.dataset.showLabel;
    input.type = reveal ? 'text' : 'password';
    source.setAttribute('aria-pressed', String(reveal));

    const labelElement = source.querySelector('[data-password-toggle-label]');
    if (label && labelElement) labelElement.textContent = label;
  }

  #resetContent() {
    const form = this.#dialog.closest('form');
    form?.reset();

    for (const source of this.#dialog.querySelectorAll('button[command="--toggle-password"]')) {
      const inputId = source.getAttribute('aria-controls');
      const input = inputId ? document.getElementById(inputId) : null;
      if (input instanceof HTMLInputElement && this.#dialog.contains(input)) input.type = 'password';
      source.setAttribute('aria-pressed', 'false');

      const label = source.dataset.showLabel;
      const labelElement = source.querySelector('[data-password-toggle-label]');
      if (label && labelElement) labelElement.textContent = label;
    }

    const errors = [...this.#dialog.querySelectorAll('[data-dialog-error]')];
    const errorIds = new Set(errors.map((error) => error.id).filter(Boolean));
    for (const error of errors) error.hidden = true;

    for (const control of this.#dialog.querySelectorAll('[aria-invalid="true"]')) {
      control.removeAttribute('aria-invalid');
      const describedBy = (control.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && !errorIds.has(id));
      if (describedBy.length > 0) control.setAttribute('aria-describedby', describedBy.join(' '));
      else control.removeAttribute('aria-describedby');
    }
  }

  #restoreFocus() {
    const trigger = this.#triggerElement;
    const triggerId = this.#triggerElementId;
    this.#triggerElement = null;
    this.#triggerElementId = null;

    if (this.#canRestoreFocus(trigger)) {
      trigger.focus();
      return;
    }

    const replacement = triggerId ? document.getElementById(triggerId) : null;
    if (this.#canRestoreFocus(replacement)) replacement.focus();
  }

  #canRestoreFocus(element) {
    return element instanceof HTMLElement
      && element.isConnected
      && element !== document.body
      && element !== document.documentElement
      && !element.hidden;
  }

  #setupBreakpointListener() {
    const breakpoints = { sm: '40rem', md: '48rem', lg: '64rem', xl: '80rem' };

    for (const [bp, width] of Object.entries(breakpoints)) {
      if (this.#dialog.classList.contains(`${bp}:hidden`)) {
        const mq = window.matchMedia(`(min-width: ${width})`);
        const handler = (e) => { if (e.matches && this.#dialog.open) this.#dialog.close(); };
        mq.addEventListener('change', handler);
        this.#ac.signal.addEventListener('abort', () => mq.removeEventListener('change', handler));
        return;
      }
      if (this.#dialog.classList.contains(`max-${bp}:hidden`)) {
        const mq = window.matchMedia(`(width < ${width})`);
        const handler = (e) => { if (e.matches && this.#dialog.open) this.#dialog.close(); };
        mq.addEventListener('change', handler);
        this.#ac.signal.addEventListener('abort', () => mq.removeEventListener('change', handler));
        return;
      }
    }
  }
}

customElements.define('s-dialog', SDialog);
